import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle, faUser } from "@fortawesome/pro-regular-svg-icons";
import { faBan } from "@fortawesome/pro-solid-svg-icons";

import useHover from "hooks/events/useHover";
import { CustomIcon } from "components/customIcon/CustomIcon";
import { SkeletonBar } from "components/skeleton";

export interface IStatisticsBarNoShows {
  total: number;
  no_show_charge: number;
  no_show_defer: number;
  no_show_weather: number;
}

export interface IStatisticsBarBookingSources {
  tee_sheet: number;
  online: number;
  mobile: number;
  phone: number;
  walk_in: number;
  booking_engine: number;
  golfnow: number;
  play_golf_canada: number;
  other: number;
}

export const EMPTY_BOOKING_SOURCES: IStatisticsBarBookingSources = {
  tee_sheet: 0,
  online: 0,
  mobile: 0,
  phone: 0,
  walk_in: 0,
  booking_engine: 0,
  golfnow: 0,
  play_golf_canada: 0,
  other: 0,
};

const BOOKING_SOURCE_LABELS: Array<{ key: keyof IStatisticsBarBookingSources; label: string }> = [
  { key: "tee_sheet", label: "Tee Sheet" },
  { key: "online", label: "Online" },
  { key: "mobile", label: "Mobile" },
  { key: "phone", label: "Phone" },
  { key: "walk_in", label: "Walk In" },
  { key: "booking_engine", label: "Booking Engine" },
  { key: "golfnow", label: "GolfNow" },
  { key: "play_golf_canada", label: "Play Golf Canada" },
  { key: "other", label: "Other" },
];

interface IStatisticsBarBookingSummaryProps {
  bookedCount: number;
  availableCount: number;
  noShows: IStatisticsBarNoShows;
  bookingSources?: IStatisticsBarBookingSources;
  cartCount?: number;
  onCartClick?: () => void;
  isLoading?: boolean;
  compact?: boolean;
}

export default function StatisticsBarBookingSummary(props: IStatisticsBarBookingSummaryProps) {
  const { t } = useTranslation();
  const bookedRef = useRef<HTMLDivElement>(null);
  const bookedHovered = useHover(bookedRef);
  const noShowsRef = useRef<HTMLDivElement>(null);
  const noShowsHovered = useHover(noShowsRef);

  const totalSlots = props.bookedCount + props.availableCount;
  const bookedPercent =
    totalSlots > 0 ? Math.round((props.bookedCount / totalSlots) * 100) : 0;
  const cartCount = props.cartCount ?? 0;
  const bookingSources = props.bookingSources ?? EMPTY_BOOKING_SOURCES;
  const sourceBreakdownItems = BOOKING_SOURCE_LABELS.filter(item => bookingSources[item.key] > 0);

  const sourceHoverContent = (
    <div
      className={`statistics-bar-booking-summary-hover ${bookedHovered ? "statistics-bar-booking-summary-hover--visible" : ""}`}
    >
      <ul className="information-item-hover-content">
        {sourceBreakdownItems.length > 0 ? (
          sourceBreakdownItems.map(item => (
            <li key={item.key}>
              <p className="type">{t(item.label)}</p>
              <p className="value">{bookingSources[item.key]}</p>
            </li>
          ))
        ) : (
          <li>
            <p className="type">{t("No bookings")}</p>
            <p className="value">0</p>
          </li>
        )}
      </ul>
    </div>
  );

  if (props.isLoading) {
    if (props.compact) {
      return (
        <div className="statistics-bar-booking-summary statistics-bar-booking-summary--compact statistics-bar-booking-summary--loading">
          <SkeletonBar
            className="statistics-bar-booking-summary-skeleton-segment statistics-bar-booking-summary-skeleton-segment--booked"
            height="1.125rem"
          />
          <SkeletonBar
            className="statistics-bar-booking-summary-skeleton-segment statistics-bar-booking-summary-skeleton-segment--no-shows"
            height="1.125rem"
          />
          <SkeletonBar
            className="statistics-bar-booking-summary-skeleton-segment statistics-bar-booking-summary-skeleton-segment--carts"
            height="1.125rem"
          />
        </div>
      );
    }

    return (
      <SkeletonBar
        className="statistics-bar-booking-summary-skeleton"
        height="1.5rem"
      />
    );
  }

  if (props.compact) {
    return (
      <div className="statistics-bar-booking-summary statistics-bar-booking-summary--compact">
        <div
          ref={bookedRef}
          className="statistics-bar-booking-summary-segment statistics-bar-booking-summary-segment--booked"
        >
          <FontAwesomeIcon icon={faUser} className="statistics-bar-booking-summary-icon" />
          <span className="statistics-bar-booking-summary-value">
            {props.bookedCount}/{totalSlots}
          </span>
          {sourceHoverContent}
        </div>

        <span className="statistics-bar-booking-summary-separator" aria-hidden="true">
          ·
        </span>

        <div
          ref={noShowsRef}
          className="statistics-bar-booking-summary-segment statistics-bar-booking-summary-segment--no-shows"
        >
          <FontAwesomeIcon icon={faBan} className="statistics-bar-booking-summary-icon" />
          <span className="statistics-bar-booking-summary-value">{props.noShows.total}</span>
          <div
            className={`statistics-bar-booking-summary-hover ${noShowsHovered ? "statistics-bar-booking-summary-hover--visible" : ""}`}
          >
            <ul className="information-item-hover-content">
              <li>
                <p className="type">{t("Charge")}</p>
                <p className="value">{props.noShows.no_show_charge}</p>
              </li>
              <li>
                <p className="type">{t("Deferred")}</p>
                <p className="value">{props.noShows.no_show_defer}</p>
              </li>
              <li>
                <p className="type">{t("Weather")}</p>
                <p className="value">{props.noShows.no_show_weather}</p>
              </li>
            </ul>
          </div>
        </div>

        <span className="statistics-bar-booking-summary-separator" aria-hidden="true">
          ·
        </span>
        <div
          className="statistics-bar-booking-summary-segment statistics-bar-booking-summary-segment--carts"
          onClick={props.onCartClick}
          style={{ cursor: props.onCartClick ? "pointer" : undefined }}
        >
          <span className="statistics-bar-booking-summary-icon statistics-bar-booking-summary-icon--cart">
            <CustomIcon src="../../../public/images/powercart2-01.svg" size="small" />
          </span>
          <span className="statistics-bar-booking-summary-value">{cartCount}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="statistics-bar-booking-summary">
      <div
        ref={bookedRef}
        className="statistics-bar-booking-summary-segment statistics-bar-booking-summary-segment--booked"
      >
        <FontAwesomeIcon icon={faUser} className="statistics-bar-booking-summary-icon statistics-bar-booking-summary-icon--tablet" />
        <span className="statistics-bar-booking-summary-value">{props.bookedCount}</span>
        <span className="statistics-bar-booking-summary-value">{t("of")}</span>
        <span className="statistics-bar-booking-summary-value">{totalSlots}</span>
        <span className="statistics-bar-booking-summary-label statistics-bar-booking-summary-label--desktop">
          {t("booked")}
        </span>
        <span className="statistics-bar-booking-summary-value">({bookedPercent}%)</span>
        <FontAwesomeIcon
          icon={faInfoCircle}
          className="statistics-bar-booking-summary-info-icon statistics-bar-booking-summary-label--desktop"
        />
        {sourceHoverContent}
      </div>

      <span className="statistics-bar-booking-summary-separator" aria-hidden="true">
        ·
      </span>

      <div
        ref={noShowsRef}
        className="statistics-bar-booking-summary-segment statistics-bar-booking-summary-segment--no-shows"
      >
        <FontAwesomeIcon
          icon={faBan}
          className="statistics-bar-booking-summary-icon statistics-bar-booking-summary-icon--tablet"
        />
        <span className="statistics-bar-booking-summary-value">{props.noShows.total}</span>
        <span className="statistics-bar-booking-summary-label statistics-bar-booking-summary-label--desktop">
          {props.noShows.total === 1 ? t("no-show") : t("no-shows")}
        </span>
        <FontAwesomeIcon
          icon={faInfoCircle}
          className="statistics-bar-booking-summary-info-icon statistics-bar-booking-summary-label--desktop"
        />
        <div
          className={`statistics-bar-booking-summary-hover ${noShowsHovered ? "statistics-bar-booking-summary-hover--visible" : ""}`}
        >
          <ul className="information-item-hover-content">
            <li>
              <p className="type">{t("Charge")}</p>
              <p className="value">{props.noShows.no_show_charge}</p>
            </li>
            <li>
              <p className="type">{t("Deferred")}</p>
              <p className="value">{props.noShows.no_show_defer}</p>
            </li>
            <li>
              <p className="type">{t("Weather")}</p>
              <p className="value">{props.noShows.no_show_weather}</p>
            </li>
          </ul>
        </div>
      </div>

      <span className="statistics-bar-booking-summary-separator" aria-hidden="true">
        ·
      </span>
      <div
        className="statistics-bar-booking-summary-segment statistics-bar-booking-summary-segment--carts"
        onClick={props.onCartClick}
        style={{ cursor: props.onCartClick ? "pointer" : undefined }}
      >
        <span className="statistics-bar-booking-summary-icon statistics-bar-booking-summary-icon--tablet statistics-bar-booking-summary-icon--cart">
          <CustomIcon src="../../../public/images/powercart2-01.svg" size="small" />
        </span>
        <span className="statistics-bar-booking-summary-value">{cartCount}</span>
        <span className="statistics-bar-booking-summary-label statistics-bar-booking-summary-label--desktop">
          {cartCount === 1 ? t("cart") : t("carts")}
        </span>
      </div>
    </div>
  );
}
